import { calculateItemTaxTotal, getInclusiveTax, getItemGrandTotal, getItemSubtotal, getModSum } from "../../../helpers/calc";
import promotionService from "../../../services/promotion-service";
import PromotionHelper from "../../../helpers/promotionHelper";
import { toFixed } from "../../../helpers/utils";
import arraysEqual from "../../../utils/arraysEqual";

export default {
  data () {
    return {
      modifiersShow: false,
      modifiers: {
        modifierGroupsAtCurrentItem: [],
        modifierIdsAtCurrentItem: [],
        isNewItem: false
      },
      skipModifierValidation: false
    };
  },
  methods: {
    modifiersOpen (itemId, isNewItem = false) {
      const index = isNewItem ? (this.currentOrder.items.length - 1) : this.currentOrder.items.findIndex(currentItem => currentItem.id === itemId);

      // Check if item exists
      if (index === -1 || !this.currentOrder.items[index]) {
        console.warn('Cannot open modifiers - item not found:', itemId);
        return;
      }

      const product = this.products.find(prod => prod.id === this.currentOrder.items[index].product);
      this.modifiersShow = true;
      this.modifiers.product = product;
      this.modifiers.isNewItem = isNewItem;
      this.modifiers.currentItemIndex = index;
      this.modifiers.modifierIdsAtCurrentItem = [];
      for (const mod of this.currentOrder.items[index].modifiers) {
        if (!mod.void_status) {
          this.modifiers.modifierIdsAtCurrentItem.push(mod.oryginal);
        }
      }
      this.modifiers.modifierGroupsAtCurrentItem = this.getProductModifierGroups(
        this.currentOrder.items[index].product);

      if (isNewItem) {
        const item = this.currentOrder.items[index];
        for (const group of this.modifiers.modifierGroupsAtCurrentItem) {
          for (const modifier of group.modifiers) {
            if (!modifier.is_default) continue;
            let modPrice = JSON.parse(JSON.stringify(modifier.price));
            if (this.is_tax_inclusive) {
              let taxSum = 0;
              for (const tax_rate of (this.modifiers.product?.tax_rates || [])) {
                taxSum += parseFloat(toFixed(getInclusiveTax(modifier.price, tax_rate.rate / 100), 2));
              }
              modPrice = parseFloat(toFixed(modifier.price - taxSum, 2));
            }
            item.modifiers.push({
              name: modifier.name,
              price: modPrice,
              price_original: modifier.price,
              cost: modifier.cost,
              group: group.id,
              oryginal: modifier.id,
              void_status: false,
              // Freshly added at menu price; calculateItemCashDiscountChange
              // converts it once when cash discount is active.
              cd_adjusted: false
            });
          }
        }
      }
    },
    async modifiersSave () {
      // Skip validation if we're deleting the item
      if (this.skipModifierValidation) {
        this.skipModifierValidation = false;
        return false;
      }

      const index = this.modifiers.currentItemIndex;
      const item = this.currentOrder.items[index];

      // Check if item still exists (could have been removed/voided while modal was open)
      if (!item || item.void_status === 1) {
        console.warn('Item no longer exists or is voided at index', index, '- closing modifier modal');
        this.modifiersShow = false;
        return false;
      }

      const hasErrors = [];
      this.modifiers.modifierGroupsAtCurrentItem.forEach(modGroup =>
        hasErrors.push(!this.isValidCountSelectedModifiersInGroup(modGroup))
      );
      if (hasErrors.filter(err => err === true).length) return false;
      const modifiersAtSaveStage = [];
      for (const mod of item.modifiers) {
        if (!mod.void_status) {
          const modToSave = mod.oryginal;
          modifiersAtSaveStage.push(modToSave);
        }
      }
      if (!this.currentOrder.items[index].has_kitchen_updates && !arraysEqual(
        modifiersAtSaveStage.sort(),
        JSON.parse(JSON.stringify(this.modifiers.modifierIdsAtCurrentItem.sort())))
      ) {
        this.markUpdated(this.modifiers.currentItemIndex, "modifier change");
      }
      this.modifiersShow = false;
      this.currentOrder.items[index].subtotal = getItemSubtotal(this.currentOrder.items[index]);
      this.currentOrder.items[index].grand_total = parseFloat((getItemGrandTotal(this.currentOrder.items[index])).toFixed(2));
      this.currentOrder.items[index].tax = this.currentOrder.items[index].tax ? calculateItemTaxTotal(this.currentOrder.items[index]) : 0;
      // Re-apply promotions/discounts now that modifiers are chosen (only for normal orders)
      try {
        const isRefundContext = !!(this.$store.getters['orders/refundOrderId']);
        if (!isRefundContext) {
          // Re-apply product-level promotions for this specific item
          const product = this.products.find(p => p.id === item.product);
          if (product) {
            const promotions = this.$store.getters['promotions/activePromotions'] || [];
            const applicable = promotionService.getApplicablePromotions(product, promotions);
            if (applicable && applicable.length > 0) {
              const aopItemShape = {
                product: item.product,
                name: item.name,
                price: item.price,
                quantity: item.quantity || 1,
                discount: 0,
                modifiers: item.modifiers || []
              };
              const updated = PromotionHelper.applyProductPromotions(aopItemShape, product, applicable);
              if (updated) {
                this.currentOrder.items[index].discount = updated.discount || 0;
                this.currentOrder.items[index].promotion_applied = updated.promotion_applied || false;
                this.currentOrder.items[index].applied_promotions = updated.applied_promotions || [];
              }
            }
          }
          // Re-evaluate order-level promotions for the whole order
          await this.$parent.reEvaluatePromotions?.();
        }
      } catch (e) {
        // ignore
      }
      // Recalculate per-item cash discount distribution and totals. Pass the real
      // new-item flag: for an existing item this only converts freshly added
      // modifiers, it must not re-discount the item or its existing modifiers.
      await this.calculateItemCashDiscountChange(index, this.modifiers.isNewItem);
      if (this.modifiers.isNewItem && typeof this.tryGroupOrderItemAtIndex === "function") {
        const groupedItemIndex = this.tryGroupOrderItemAtIndex(index);
        if (groupedItemIndex !== -1) {
          await this.reEvaluatePromotions?.();
          await this.countSubtotal();
          this.scheduleOrderSync();
          return;
        }
      }
      await this.countSubtotal()
      this.scheduleOrderSync();
    },
    deleteItemFromModifiers () {
      /**
       * This function is used to delete/remove an item from the modifier popup
       * It removes the item from the order and closes the modifier popup
       * No validation is performed since we're deleting the item
       */
      const index = this.modifiers.currentItemIndex;
      const item = this.currentOrder.items[index];

      // Check if item exists
      if (!item) {
        console.warn('Item no longer exists at index', index, '- closing modifier modal');
        this.modifiersShow = false;
        return false;
      }

      // Get the item ID to remove
      const itemId = item.id;

      // Set flag to skip validation when the watcher triggers modifiersSave
      this.skipModifierValidation = true;

      // Close the modifier popup (the watcher will call modifiersSave but it will be skipped)
      this.modifiersShow = false;

      // Remove the item from the order
      this.removeItemFromOrderProcessing(itemId);
    },
    isValidCountSelectedModifiersInGroup (modGroup) {
      const { min_selection, max_selection } = modGroup;
      const countSelectedModifiersInGroup = this.countSelectedModifiers(modGroup);
      if (!min_selection) return true;
      if (min_selection && (min_selection > countSelectedModifiersInGroup)) {
        this.dispatchModifiersError(
          `You must select at least ${min_selection} modifiers in group ${modGroup.name}`
        );
        return false;
      }

      if (max_selection && (countSelectedModifiersInGroup > max_selection)) {
        this.dispatchModifiersError(
          `You can only select up to ${max_selection} modifiers for maximum in group ${modGroup.name}`
        );
        return false;
      }
      return true;
    },
    removeModFromItem (modifier, modGroup) {
      this.currentOrder.items[this.modifiers.currentItemIndex].modifiers.map(itemModifier => {
        if (modifier.id === itemModifier.oryginal && itemModifier.group === modGroup.id)
          itemModifier.void_status = true;
      });
    },
    isModifierSelected (modifier, modGroup) {
      const item = this.currentOrder.items[this.modifiers.currentItemIndex];
      if (!item || !item.modifiers) {
        return false;
      }
      return item.modifiers.find(
        mod => mod.oryginal === modifier.id && !mod.void_status && mod.group === modGroup.id);
    },
    async toggleModifierForItem (modifier, modifierGroup) {
      const product = this.modifiers.product;
      if (
        product &&
        this.$store.getters["products86d/isModifier86d"](product.id, modifier.id)
      ) {
        const terminal = this.$store.getters["config/terminal"];
        const is86Controls =
          terminal?.db_settings?.find(s => s.name === "is_86")?.value === "true";
        this.dispatchModifiersError(
          is86Controls
            ? "This modifier is unavailable (86). Staff can change availability with a long-press on the product tile."
            : "This modifier is unavailable (86)."
        );
        return;
      }
      if (this.isModifierSelected(modifier, modifierGroup)) {
        this.removeModFromItem(modifier, modifierGroup);
      } else {
        // Check if we've reached the max selection for this group
        const countSelected = this.countSelectedModifiers(modifierGroup);
        const maxSelection = modifierGroup.max_selection;

        // If max is reached, remove the first modifier from this group before adding new one
        if (maxSelection && countSelected >= maxSelection) {
          // Find and remove the first modifier from this group
          const currentModifiers = this.currentOrder.items[this.modifiers.currentItemIndex].modifiers;
          const indexToRemove = currentModifiers.findIndex(mod => mod.group === modifierGroup.id && !mod.void_status);
          if (indexToRemove !== -1) {
            currentModifiers.splice(indexToRemove, 1);
          }
        }

        let modPrice = JSON.parse(JSON.stringify(modifier.price));
        if (this.is_tax_inclusive) {
          let taxSum = 0;
          for (const tax_rate of this.modifiers.product["tax_rates"]) {
            taxSum += parseFloat(toFixed(getInclusiveTax(modifier.price, tax_rate.rate / 100), 2));
          }
          modPrice = parseFloat(toFixed(modifier.price - taxSum, 2));
        }
        const orderModifier = {
          "name": modifier.name,
          "price": modPrice,
          "price_original": modifier.price,
          "cost": modifier.cost,
          "group": modifierGroup.id,
          "oryginal": modifier.id,
          "void_status": false,
          // Freshly added at menu price; calculateItemCashDiscountChange
          // converts it once when cash discount is active.
          "cd_adjusted": false
        };
        this.currentOrder.items[this.modifiers.currentItemIndex].modifiers.push(orderModifier);
      }
      const item = this.currentOrder.items[this.modifiers.currentItemIndex];
      this.currentOrder.items[this.modifiers.currentItemIndex].grand_total = parseFloat((item.subtotal + (await getModSum(item)) * item.quantity));

      this.countSubtotal();
      this.$forceUpdate();
    },
    hasAbilityToAddNewModifier (maxSelection, modifierGroup) {
      const countSelectedModifiers = this.countSelectedModifiers(modifierGroup);
      if (!maxSelection) return true;
      if (countSelectedModifiers >= maxSelection) {
        this.dispatchModifiersError(`You can only select up to ${maxSelection} modifiers for maximum`);
        return false;
      }

      return true;
    },
    countSelectedModifiers (modifierGroup) {
      const item = this.currentOrder.items[this.modifiers.currentItemIndex];

      // Check if item still exists (could have been removed/voided while modal was open)
      if (!item || !item.modifiers) {
        console.warn('Item or modifiers no longer exist at index', this.modifiers.currentItemIndex);
        return 0;
      }

      const activeGroupModifiers = item.modifiers
        .filter(mod => mod.group === modifierGroup.id && !mod.void_status);

      return activeGroupModifiers.length;
    },
    getProductModifierGroups: function (id) {
      const modifierGroups = this.products.find(item => item.id === id).modifier_groups;
      // Sort modifiers within each group by 'sort' field, fallback to alphabetical
      modifierGroups.forEach(group => {
        group.modifiers.sort((a, b) => {
          const aSort = (a.sort !== undefined && a.sort !== null) ? a.sort : Infinity;
          const bSort = (b.sort !== undefined && b.sort !== null) ? b.sort : Infinity;
          // First sort by 'sort' field if available
          if (aSort !== bSort) {
            return aSort - bSort;
          }
          // If sort values are the same or both missing, sort alphabetically by name
          return (a.name || '').localeCompare(b.name || '');
        });
      });

      // Sort modifier groups by 'sorting' field, fallback to alphabetical
      modifierGroups.sort((a, b) => {
        const aSorting = (a.sorting !== undefined && a.sorting !== null) ? a.sorting : Infinity;
        const bSorting = (b.sorting !== undefined && b.sorting !== null) ? b.sorting : Infinity;
        if (aSorting !== bSorting) {
          return aSorting - bSorting;
        }
        return (a.name || '').localeCompare(b.name || '');
      });

      return modifierGroups;
    },
    getModifiersByItemPosition: function (itemId) {
      const itemIndex = this.currentOrder.items.findIndex(item => item.id === itemId);
      if (itemIndex === -1) return [];
      const modGroups = [];
      const allModsAtItem = this.currentOrder.items[itemIndex].modifiers || [];
      const currentProductId = this.currentOrder.items[itemIndex].product;
      const current_product = this.products.find(product => product.id.toString() === currentProductId.toString());

      // find groups
      for (const mod of allModsAtItem) {
        if (!mod.void_status) {
          if (!modGroups.some(group => group.id === mod.group)) {
            // The product or its modifier group may no longer exist locally if the
            // modifier/group was edited or removed from the product in the menu after
            // this order item was created. The modifier line still carries its own
            // name/price, so fall back to a label instead of crashing the render.
            const group = current_product
              ? current_product["modifier_groups"].find(modGroup => modGroup.id === mod.group)
              : null;
            const groupName = (group && group.name) || mod.group_name || "Modifiers";
            modGroups.push({ id: mod.group, name: groupName, modifiers: [] });
          }
          modGroups.find(group => group.id === mod.group).modifiers.push({
            oryginal: mod.oryginal,
            name: mod.name,
            price: mod.price,
            price_before_tax: mod.price_before_tax
          });
        }
      }
      return modGroups;
    },
    dispatchModifiersError (info) {
      this.$store.dispatch("errorModal/displayModalError", {
        modalId: "modifier-error",
        errorBody: {
          title: "Modifiers",
          info
        }
      });
    },
    async savePredefinedModifiers(modifiers, modifier_groups, itemIndex=0){
      // declaring modifier array
      let modifierArray = []
      // iterate through refund modifiers
      modifiers.forEach(modifier=>{
        // Search in all modifier groups, not just the first one
        let selectedModifier = null;

        // Check all modifier groups for the matching modifier
        for (const modifierGroup of modifier_groups) {
          const foundModifier = modifierGroup.modifiers.find(
            selectedModifier => selectedModifier.id === modifier.oryginal
          );

          if (foundModifier) {
            selectedModifier = foundModifier;
            // Save the group for later use
            selectedModifier._group = modifierGroup;
            break;
          }
        }

        // find the selected modifers from the modifier_groups' list and push them up the modifiersArray
        if(selectedModifier)
          modifierArray.push(selectedModifier)
      })

      // define the index of item having modifiers
      this.modifiers.currentItemIndex = itemIndex

      // Clear existing modifiers first to avoid duplicates
      this.currentOrder.items[itemIndex].modifiers = [];

      // iterate though all the modifiers in the array and insert into currentOrder's specific item
      modifierArray.forEach(modifier=>{
        // Use the saved group or fall back to first group
        const group = modifier._group || modifier_groups[0];
        delete modifier._group; // Clean up temporary property

        // Directly add modifier without toggle logic
        let modPrice = JSON.parse(JSON.stringify(modifier.price));
        if (this.is_tax_inclusive) {
          let taxSum = 0;
          for (const tax_rate of this.modifiers.product["tax_rates"]) {
            taxSum += parseFloat(toFixed(getInclusiveTax(modifier.price, tax_rate.rate / 100), 2));
          }
          modPrice = parseFloat(toFixed(modifier.price - taxSum, 2));
        }
        const orderModifier = {
          "name": modifier.name,
          "price": modPrice,
          "price_original": modifier.price,
          "cost": modifier.cost,
          "group": group.id,
          "oryginal": modifier.id,
          "void_status": false,
          // Freshly added at menu price; calculateItemCashDiscountChange
          // converts it once when cash discount is active.
          "cd_adjusted": false
        };
        this.currentOrder.items[itemIndex].modifiers.push(orderModifier);
      })

      // Save modifiers once after all are added
      await this.modifiersSave()
    }
  }
};
